import { useEffect, useMemo, useState, useRef } from "react";
import { AssignmentSubmission, SelectOption } from "types";
import {
  useCurrentUser,
  useFetchSubmissions,
  useGetSubmissions,
  useGetPatients,
  useGetAssignments,
  useGetStats,
  useFetchResourceStats,
} from "utils/hooks";
import { generateStatsData } from "pages/AppPages/TherapistPages/Reports/components/ResourceStatsChart/helpers";

const resourceTypeOptions = [
  { value: "all", label: "All Types" },
  { value: "assessment", label: "Assessments" },
  { value: "worksheet", label: "Worksheets" },
  { value: "infosheet", label: "Infosheets" },
  { value: "linkFile", label: "Links and Files" },
];

const getStartDate = () => {
  // Initial date range is "This week". So next code gets start date for this date filter
  const start: Date | null = new Date();
  const now = new Date();
  const nowWeekDay = now.getDay();
  start.setDate(now.getDate() - nowWeekDay);
  start.setHours(0, 0, 0, 0);
  return start;
};

const useSubmissionUtils = (groupId?: string) => {
  const dataRef = useRef(false);
  const { isTherapist, user } = useCurrentUser();
  const { fetchAllSubmissions } = useFetchSubmissions();
  const { patients } = useGetPatients({});
  const { hasAssignments } = useGetAssignments({});
  const { resourcesStats } = useGetStats();

  const { fetchResourceStats, loading: statsLoading } = useFetchResourceStats();
  const [currentPatient, setCurrentPatient] = useState<any>(null);
  const { submissions: patientSubmissions, groupSubmissions } =
    useGetSubmissions({ groupId });

  const submissions = groupId ? groupSubmissions : patientSubmissions;

  const [filteredSubmissions, setFilteredSubmissions] = useState(submissions);
  const memoizedSubmissions = useMemo(
    () => filteredSubmissions,
    [filteredSubmissions]
  );
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    getStartDate(),
    new Date(),
  ]);
  const [startDate, endDate] = dateRange;
  const [selectedResourceType, setSelectedResourceType] = useState(
    resourceTypeOptions[0]
  );
  const [selectedPatient, setSelectedPatient] = useState<Array<SelectOption>>(
    []
  );
  const [statsData, setStatsData] = useState<Array<any>>([]);
  const [xLabel, setXLabel] = useState("Day");
  const [dateBtnValue, setDateBtnValue] = useState<string>("This week");

  const sortedSubmissions = memoizedSubmissions?.length
    ? memoizedSubmissions
        .slice()
        .sort((a: { createdAt: number }, b: { createdAt: number }) =>
          b.createdAt > a.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0
        )
        .filter((s: AssignmentSubmission) => s.userId)
    : null;

  useEffect(() => {
    if (dataRef.current) return;
    fetchAllSubmissions();
    dataRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilteredSubmissions(submissions);
  }, [submissions]);

  useEffect(() => {
    // Only fetch resource stats for therapists
    if (isTherapist) {
      fetchResourceStats({
        resourceType: "all", // Dashboard page has the Submissions stats card that should show submissions amount for the selected dates. That is why we always  have to fetch all types
        userId: [],
        startDate: startDate !== null ? startDate.getTime().toString() : "",
        endDate: endDate !== null ? endDate.getTime().toString() : "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, isTherapist]);

  useEffect(() => {
    // Only generate stats for therapists
    if (!isTherapist || !resourcesStats || !startDate || !endDate) return;

    generateStatsData(
      startDate,
      endDate,
      selectedResourceType,
      setXLabel,
      resourcesStats,
      setStatsData,
      selectedPatient
    );
  }, [
    isTherapist,
    resourcesStats,
    selectedResourceType,
    startDate,
    endDate,
    selectedPatient,
  ]);

  const handleSearch = (searchedSubmissions: any) => {
    setFilteredSubmissions(searchedSubmissions);
  };

  const handlePatientChange = (patient: any) => {
    const patientSubmissions = submissions.filter(
      (submission: { userId: any }) => submission.userId === patient?.value
    );

    setCurrentPatient(patient);
    if (patient) {
      setFilteredSubmissions(patientSubmissions);
    } else {
      setFilteredSubmissions(submissions);
    }
  };

  const hasSubmissions = submissions?.length > 0;
  const hasAPatient = patients?.length === 1;
  const hasPatients = patients?.length > 1;
  const hasAnyPatients = hasAPatient || hasPatients;

  const firstPatient = patients && patients[0];

  return {
    hasSubmissions,
    submissions,
    currentPatient,
    handleSearch,
    handlePatientChange,
    sortedSubmissions,
    firstName: user?.firstName,
    showFilter: isTherapist && hasPatients,
    showTableElements: hasSubmissions,
    showConfirmEmailBar: true,
    hasAnyPatients,
    firstPatient,
    hasAssignments,
    dateRange,
    setDateRange,
    selectedResourceType,
    setSelectedResourceType,
    selectedPatient,
    setSelectedPatient,
    statsData,
    xLabel,
    statsLoading,
    dateBtnValue,
    setDateBtnValue,
    isTherapist,
  };
};

export default useSubmissionUtils;
